/**
 * Boot purge of WorkOS refresh tokens that belong to another client id.
 *
 * Runs from main.tsx BEFORE AuthKitProvider mounts, so before authkit-js reads
 * localStorage. The portal runs authkit-js with `devMode: true`, which keeps
 * the refresh token in localStorage under `workos:refresh-token:<clientId>`
 * (authkit-js 0.20 `session-data.ts`). On start-up authkit-js reads that key
 * and falls back to the UNSCOPED legacy key `workos:refresh-token`; a token
 * minted by a different WorkOS application is rejected at refresh, so the
 * portal must not hand it one. This module removes:
 *
 *   - the unscoped legacy key (its owner is unknown, and every session written
 *     since the scoped key landed lives under the scoped key);
 *   - every `workos:refresh-token:<id>` where `<id>` is neither the current
 *     client id nor listed in `VITE_WORKOS_SPARE_REFRESH_CLIENT_IDS`.
 *
 * and records the current client id under {@link CLIENT_MARKER_KEY}, so a
 * support session can see which client minted the stored session.
 *
 * Spare list. The switch to the portal's own WorkOS application sets
 * `VITE_WORKOS_SPARE_REFRESH_CLIENT_IDS` to the web app's client id for a
 * 14-day soak, so rolling back to the old client id stays a silent sign-in
 * (its token survives the purge). REMOVE the variable, and this note, once the
 * soak is over (14 days after the switch ships with no rollback): the cleanup
 * deploy then purges the web app's token like any other foreign one.
 *
 * Today's deploy is a no-op: the only key present is the current client's.
 *
 * Contract: idempotent, never throws (storage may be missing, full or
 * blocked), touches nothing outside the keys above. It does nothing at all
 * when the current client id is empty, since "not the current id" would then
 * match every key.
 */

/** authkit-js `storageKeys.refreshToken`; the scoped key is `<this>:<clientId>`. */
export const LEGACY_REFRESH_TOKEN_KEY = 'workos:refresh-token';
const SCOPED_PREFIX = `${LEGACY_REFRESH_TOKEN_KEY}:`;

/** Which client id minted the stored session; written by this module only. */
export const CLIENT_MARKER_KEY = 'mt:workos-client-id';

/** The slice of `Storage` the purge uses (a fake in tests). */
export type PurgeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

export type PurgeResult = {
  /** Keys removed, in storage order. Never holds a token value. */
  removed: string[];
  /** True when the marker was written (absent or different before). */
  marked: boolean;
};

/** `a, b,,a` → `['a', 'b']`: trimmed, empties and duplicates dropped. */
export function parseClientIdList(raw: string | undefined | null): string[] {
  if (!raw) return [];
  return [...new Set(raw.split(',').map((s) => s.trim()).filter(Boolean))];
}

function defaultStorage(): PurgeStorage | undefined {
  try {
    return window.localStorage; // the getter itself throws when storage is blocked
  } catch {
    return undefined;
  }
}

export function purgeStaleWorkosSessions(opts: {
  /** The client id this build signs in with (`VITE_WORKOS_CLIENT_ID`). */
  clientId: string | undefined;
  /** Other client ids whose refresh token stays (`VITE_WORKOS_SPARE_REFRESH_CLIENT_IDS`). */
  spareClientIds?: readonly string[];
  /** Defaults to `window.localStorage`. */
  storage?: PurgeStorage;
}): PurgeResult {
  const result: PurgeResult = { removed: [], marked: false };
  const clientId = opts.clientId?.trim();
  if (!clientId) return result;

  try {
    const storage = opts.storage ?? defaultStorage();
    if (!storage) return result;
    const keep = new Set([clientId, ...(opts.spareClientIds ?? [])]);

    // Snapshot the keys first: removing while walking by index skips entries.
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key !== null) keys.push(key);
    }

    for (const key of keys) {
      const stale =
        key === LEGACY_REFRESH_TOKEN_KEY ||
        (key.startsWith(SCOPED_PREFIX) && !keep.has(key.slice(SCOPED_PREFIX.length)));
      if (!stale) continue;
      try {
        storage.removeItem(key);
        result.removed.push(key);
      } catch {
        // One key failing must not stop the rest.
      }
    }

    try {
      if (storage.getItem(CLIENT_MARKER_KEY) !== clientId) {
        storage.setItem(CLIENT_MARKER_KEY, clientId);
        result.marked = true;
      }
    } catch {
      // Quota or blocked storage: the marker is a support aid, not a requirement.
    }
  } catch {
    // Storage unavailable: AuthKit will find nothing to read either.
  }
  return result;
}
